import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import GraphIcon from '../../../assets/icons/pulse-alert.svg?react';
import MachineIcon from '../../../assets/icons/machine-alarm.svg?react';
import { useGetMachinesAvailableSignalsQuery } from '../../../gql/generated';
import {
  formatMetricName,
  formatUnit,
  isMachineAlarmSignal,
} from '../../../utils/formatMetric';
import { NumberInput } from '../../NumberInput';
import { MachineHistoryGraph } from '../MachineHistoryGraph';
import { AlarmTriggerLogicControls } from './AlarmTriggerLogicControls';
import { TriggerLogicDocumentationPanel } from './TriggerLogicDocumentationPanel';
import { SignalTriggerSettings } from './context';
import { useAddAlarmDialog } from './useAddAlarmDialog';

interface SignalDetails {
  signal: string;
  category?: string;
  unit?: string;
}

function toSignalDefaultSettings(
  formData: ReturnType<typeof useAddAlarmDialog>['formData']
): SignalTriggerSettings {
  return {
    triggerType: formData.triggerType,
    threshold: formData.threshold,
    thresholdMax: formData.thresholdMax,
    triggerLogicType: formData.triggerLogicType,
    percentageThreshold: formData.percentageThreshold,
    evaluationWindow: formData.evaluationWindow,
    periodMinutes: formData.periodMinutes,
    numberOfSpikes: formData.numberOfSpikes,
    cooldownPeriod: formData.cooldownPeriod,
    advancedSettingsExpanded: false,
  };
}

function MachineAlarmResendControl({
  value,
  onChange,
}: {
  value?: string;
  onChange: (updates: { cooldownPeriod: string }) => void;
}) {
  return (
    <div
      className='bg-surface-primary-support border-border-secondary flex flex-col gap-3 self-start rounded-lg border p-3'
    >
      <label className='size-p2 text-primary'>Repetition</label>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='size-p2 text-secondary'>
          Resend alert if condition is still true after
        </span>
        <NumberInput
          size='compact'
          value={value || ''}
          onChange={(event) => onChange({ cooldownPeriod: event.target.value })}
          suffix='min'
        />
      </div>
    </div>
  );
}

export function AlarmConditionsStep() {
  const { formData, updateFormData, setFormData, selectedMachineIds, availableMachines } =
    useAddAlarmDialog();
  const [isTriggerLogicDocsOpen, setIsTriggerLogicDocsOpen] = useState(false);
  const isMachineAlarm = isMachineAlarmSignal({
    unit: formData.signalUnit,
    signalCategory: formData.category,
  });

  // Track if thresholds have been initialized
  const thresholdsInitialized = useRef(false);

  // Query available signals to get the unit
  const { data: metricsData } = useGetMachinesAvailableSignalsQuery({
    variables: {
      ids: selectedMachineIds,
    },
    skip: selectedMachineIds.length === 0,
  });

  const signalDetails = useMemo(() => {
    const detailsBySignal = new Map<string, SignalDetails>();

    for (const machine of metricsData?.machinesSignals?.rows || []) {
      for (const category of machine.availableSignals?.signals || []) {
        for (const metric of category.signals || []) {
          if (!metric.signal || detailsBySignal.has(metric.signal)) {
            continue;
          }

          detailsBySignal.set(metric.signal, {
            signal: metric.signal,
            category: category.category || undefined,
            unit: metric.unit ? formatUnit(metric.unit) : undefined,
          });
        }
      }
    }

    return detailsBySignal;
  }, [metricsData]);

  const selectedSignals = useMemo(() => {
    return formData.signals.map((signal) => {
      const details = signalDetails.get(signal);
      return {
        signal,
        category: details?.category || formData.category || undefined,
        unit:
          details?.unit ||
          (formData.signalUnit ? formatUnit(formData.signalUnit) : undefined),
      };
    });
  }, [formData.signals, formData.category, formData.signalUnit, signalDetails]);

  const hasPulseAlertSignals = selectedSignals.some(
    (signal) =>
      !isMachineAlarmSignal({
        unit: signal.unit,
        signalCategory: signal.category,
      })
  );

  const selectedSignal = selectedSignals[0];
  const multiSignalDefaults = toSignalDefaultSettings(formData);

  const selectedMachines = useMemo(
    () => availableMachines.filter((m) => formData.machines.includes(m.id)),
    [availableMachines, formData.machines]
  );

  useEffect(() => {
    if (selectedSignals.length <= 1) {
      return;
    }

    const existingSettings = formData.multiSignalSettings || {};
    const nextSettings: Record<string, SignalTriggerSettings> = {};
    let changed = false;

    for (const signal of selectedSignals) {
      const currentSettings = existingSettings[signal.signal];
      if (currentSettings) {
        nextSettings[signal.signal] = currentSettings;
      } else {
        changed = true;
        nextSettings[signal.signal] = { ...multiSignalDefaults };
      }
    }

    for (const existingSignal of Object.keys(existingSettings)) {
      if (!selectedSignals.some((signal) => signal.signal === existingSignal)) {
        changed = true;
        break;
      }
    }

    if (changed) {
      updateFormData({ multiSignalSettings: nextSettings });
    }
  }, [selectedSignals, formData.multiSignalSettings, multiSignalDefaults, updateFormData]);

  const getSignalSettings = (signal: string): SignalTriggerSettings => {
    return formData.multiSignalSettings?.[signal] || { ...multiSignalDefaults };
  };

  const updateSignalSettings = (
    signal: string,
    updates: Partial<SignalTriggerSettings>
  ) => {
    setFormData((prev) => {
      const currentSettings = prev.multiSignalSettings?.[signal] || toSignalDefaultSettings(prev);

      return {
        ...prev,
        multiSignalSettings: {
          ...(prev.multiSignalSettings || {}),
          [signal]: { ...currentSettings, ...updates },
        },
      };
    });
  };

  // Initialize thresholds from chart data for numeric signals only.
  const handleThresholdsCalculated = (
    thresholds: {
      minimum: number;
      maximum: number;
    },
    signalName?: string
  ) => {
    if (signalName) {
      const currentSettings = getSignalSettings(signalName);
      if (currentSettings.threshold || currentSettings.thresholdMax) {
        return;
      }

      updateSignalSettings(signalName, {
        threshold: thresholds.minimum.toString(),
        thresholdMax: thresholds.maximum.toString(),
      });
      return;
    }

    if (isMachineAlarm) return;

    if (
      !thresholdsInitialized.current &&
      !formData.threshold &&
      !formData.thresholdMax
    ) {
      thresholdsInitialized.current = true;
      updateFormData({
        threshold: thresholds.minimum.toString(),
        thresholdMax: thresholds.maximum.toString(),
      });
    }
  };

  if (selectedSignals.length > 1) {
    return (
      <>
        <div className='flex flex-col gap-4 px-4 py-6 md:px-6 md:py-8'>
          <div
            className={`grid items-center gap-4 px-1 ${
              hasPulseAlertSignals
                ? 'lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)_minmax(0,1fr)]'
                : 'lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]'
            }`}
          >
            <div className='size-caption text-secondary uppercase tracking-wide'>
              Signals ({selectedSignals.length} selected)
            </div>
            <div className='size-caption text-secondary hidden uppercase tracking-wide lg:block'>
              {hasPulseAlertSignals ? 'Trigger type' : 'Repetition'}
            </div>
            {hasPulseAlertSignals && (
              <div
                className='size-caption text-secondary hidden items-center justify-between
                  uppercase tracking-wide lg:flex'
              >
                <span>Trigger logic</span>
                <button
                  type='button'
                  aria-label='Open trigger logic documentation'
                  className='text-secondary hover:text-primary cursor-pointer rounded-full p-0.5 transition-colors outline-none focus-visible:ring-1 focus-visible:ring-blue-500'
                  onClick={() => setIsTriggerLogicDocsOpen(true)}
                >
                  <svg
                    className='h-5 w-5'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                    aria-hidden='true'
                  >
                    <circle cx='10' cy='10' r='8.25' stroke='currentColor' strokeWidth='1.5' />
                    <path
                      d='M10 8.2V13.2'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                    />
                    <circle cx='10' cy='5.8' r='0.9' fill='currentColor' />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className='divide-border-secondary flex flex-col divide-y'>
            {selectedSignals.map((signal, index) => {
              const signalIsMachineAlarm = isMachineAlarmSignal({
                unit: signal.unit,
                signalCategory: signal.category,
              });

              return (
                <div
                  key={`${signal.signal}-${index}`}
                  className={`grid gap-4 p-4 lg:gap-5 lg:p-4 ${
                    hasPulseAlertSignals
                      ? 'lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)_minmax(0,1fr)]'
                      : 'lg:grid-cols-[minmax(0,1.75fr)_minmax(0,1fr)]'
                  }`}
                >
                  <div className='flex min-w-0 flex-col'>
                    <MachineHistoryGraph
                      header={
                        <div className='flex min-w-0 items-center gap-2'>
                          {signalIsMachineAlarm ? (
                            <MachineIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
                          ) : (
                            <GraphIcon className='h-5 w-5 shrink-0' aria-hidden='true' />
                          )}
                          <h3 className='size-p2 text-primary truncate font-bold'>
                            {formatMetricName(signal.signal)}
                          </h3>
                        </div>
                      }
                      machines={selectedMachines}
                      signalCategory={signal.category}
                      signal={signal.signal}
                      unit={signal.unit}
                      onThresholdsCalculated={(thresholds) =>
                        handleThresholdsCalculated(thresholds, signal.signal)
                      }
                      alarmThresholds={{
                        minimum:
                          getSignalSettings(signal.signal).triggerType === 'minimum' ||
                          getSignalSettings(signal.signal).triggerType === 'range'
                            ? parseFloat(getSignalSettings(signal.signal).threshold) ||
                              undefined
                            : undefined,
                        maximum:
                          getSignalSettings(signal.signal).triggerType === 'maximum' ||
                          getSignalSettings(signal.signal).triggerType === 'range'
                            ? parseFloat(getSignalSettings(signal.signal).thresholdMax) ||
                              undefined
                            : undefined,
                      }}
                    />
                  </div>

                  {signalIsMachineAlarm ? (
                    <>
                      <MachineAlarmResendControl
                        value={getSignalSettings(signal.signal).cooldownPeriod}
                        onChange={(updates) => updateSignalSettings(signal.signal, updates)}
                      />
                      {hasPulseAlertSignals && (
                        <div
                          className='bg-surface-primary-support border-border-secondary hidden
                            justify-center self-start rounded-lg border p-3 lg:flex'
                        >
                          <p className='size-caption text-secondary text-center'>
                            Not applicable for this signal
                          </p>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>
                        <TriggerTypePanel
                          selectedSignalUnit={signal.unit}
                          compact
                          hideHeading
                          value={getSignalSettings(signal.signal)}
                          onChange={(updates) => updateSignalSettings(signal.signal, updates)}
                        />
                      </div>

                      <div className='flex flex-col gap-3'>
                        <AlarmTriggerLogicControls
                          compact
                          heading=''
                          idPrefix={`trigger-logic-${index}`}
                          value={getSignalSettings(signal.signal)}
                          onChange={(updates) => updateSignalSettings(signal.signal, updates)}
                          advancedSettingsExpanded={
                            getSignalSettings(signal.signal).advancedSettingsExpanded || false
                          }
                          onToggleAdvancedSettings={() =>
                            updateSignalSettings(signal.signal, {
                              advancedSettingsExpanded:
                                !getSignalSettings(signal.signal).advancedSettingsExpanded,
                            })
                          }
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {hasPulseAlertSignals && (
          <TriggerLogicDocumentationPanel
            open={isTriggerLogicDocsOpen}
            onOpenChange={setIsTriggerLogicDocsOpen}
          />
        )}
      </>
    );
  }

  if (isMachineAlarm) {
    return (
      <div className='flex flex-col gap-4 px-4 py-4 md:px-6'>
        <MachineHistoryGraph
          machines={selectedMachines}
          signalCategory={selectedSignal?.category}
          signal={selectedSignal?.signal}
          unit={selectedSignal?.unit}
        />

        <div className='border-border-primary w-full border-t' />

        <MachineAlarmResendControl
          value={formData.cooldownPeriod}
          onChange={(updates) => updateFormData(updates)}
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-4 px-4 py-4 md:px-6'>
      {/* History Graph */}
      <MachineHistoryGraph
        machines={selectedMachines}
        signalCategory={selectedSignal?.category}
        signal={selectedSignal?.signal}
        unit={selectedSignal?.unit}
        onThresholdsCalculated={handleThresholdsCalculated}
        alarmThresholds={{
          minimum:
            formData.triggerType === 'minimum' ||
            formData.triggerType === 'range'
              ? parseFloat(formData.threshold) || undefined
              : undefined,
          maximum:
            formData.triggerType === 'maximum' ||
            formData.triggerType === 'range'
              ? parseFloat(formData.thresholdMax) || undefined
              : undefined,
        }}
      />

      {/* Divider */}
      <div className='border-border-primary w-full border-t' />

      <TriggerTypePanel selectedSignalUnit={selectedSignal?.unit} />
    </div>
  );
}

function TriggerTypePanel({
  selectedSignalUnit,
  compact = false,
  hideHeading = false,
  value,
  onChange,
}: {
  selectedSignalUnit?: string;
  compact?: boolean;
  hideHeading?: boolean;
  value?: SignalTriggerSettings;
  onChange?: (updates: Partial<SignalTriggerSettings>) => void;
}) {
  const { formData, updateFormData } = useAddAlarmDialog();
  const triggerSettings = value || {
    triggerType: formData.triggerType,
    threshold: formData.threshold,
    thresholdMax: formData.thresholdMax,
    triggerLogicType: formData.triggerLogicType,
  };
  const updateTriggerSettings =
    onChange ||
    ((updates: Partial<SignalTriggerSettings>) => updateFormData(updates));

  return (
    <div className='flex flex-col'>
      {!hideHeading && <label className='size-p2 text-primary pb-2'>Trigger type</label>}

      <TriggerTypeOption
        title='Minimum'
        isSelected={triggerSettings.triggerType === 'minimum'}
        onClick={() => updateTriggerSettings({ triggerType: 'minimum' })}
        compact={compact}
      >
        {triggerSettings.triggerType === 'minimum' && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='size-caption text-secondary'>If value drops below</span>
            <NumberInput
              size='compact'
              value={triggerSettings.threshold}
              onChange={(e) => updateTriggerSettings({ threshold: e.target.value })}
              placeholder='25'
              suffix={selectedSignalUnit}
              onClick={(e) => e.stopPropagation()}
            />
            {!compact && (
              <span className='size-caption text-secondary'>
                a Pulse alert will be triggered
              </span>
            )}
          </div>
        )}
      </TriggerTypeOption>

      <TriggerTypeOption
        title='Maximum'
        isSelected={triggerSettings.triggerType === 'maximum'}
        onClick={() => updateTriggerSettings({ triggerType: 'maximum' })}
        compact={compact}
      >
        {triggerSettings.triggerType === 'maximum' && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='size-caption text-secondary'>If value exceeds</span>
            <NumberInput
              size='compact'
              value={triggerSettings.thresholdMax}
              onChange={(e) =>
                updateTriggerSettings({ thresholdMax: e.target.value })
              }
              placeholder='75'
              suffix={selectedSignalUnit}
              onClick={(e) => e.stopPropagation()}
            />
            {!compact && (
              <span className='size-caption text-secondary'>
                a Pulse alert will be triggered
              </span>
            )}
          </div>
        )}
      </TriggerTypeOption>

      <TriggerTypeOption
        title='Range'
        isSelected={triggerSettings.triggerType === 'range'}
        onClick={() => updateTriggerSettings({ triggerType: 'range' })}
        compact={compact}
      >
        {triggerSettings.triggerType === 'range' && (
          <div className='flex-col items-center gap-2'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='size-caption text-secondary text-nowrap'>
                {compact ? 'Below' : 'Trigger Pulse alert for values below'}
              </span>
              <NumberInput
                size='compact'
                value={triggerSettings.threshold}
                onChange={(e) => updateTriggerSettings({ threshold: e.target.value })}
                placeholder='25'
                suffix={selectedSignalUnit}
                onClick={(e) => e.stopPropagation()}
              />
              <span className='size-caption text-secondary text-nowrap'>or above</span>
              <NumberInput
                size='compact'
                value={triggerSettings.thresholdMax}
                onChange={(e) =>
                  updateTriggerSettings({ thresholdMax: e.target.value })
                }
                placeholder='75'
                suffix={selectedSignalUnit}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </TriggerTypeOption>
    </div>
  );
}

interface TriggerTypeOptionProps {
  title: string;
  isSelected: boolean;
  onClick: () => void;
  children?: ReactNode;
  compact?: boolean;
}

function TriggerTypeOption({
  title,
  isSelected,
  onClick,
  children,
  compact = false,
}: TriggerTypeOptionProps) {
  return (
    <button
      type='button'
      className={`flex w-full cursor-pointer gap-3 rounded-lg px-4 py-3
        text-left transition-colors outline-none focus-visible:ring-1
        focus-visible:ring-blue-500 focus-visible:ring-inset ${
          compact ? 'px-3 py-3' : ''
        } ${
          isSelected
            ? 'bg-surface-primary-support'
            : compact
              ? ''
              : 'hover:bg-surface-primary-support/50'
        }`}
      onClick={onClick}
    >
      <div className='flex shrink-0 items-start pt-0.5'>
        <div
          className={`flex h-5 w-5 items-center justify-center rounded-full
            border transition-colors ${
              isSelected
                ? 'border-focus bg-focus'
                : 'border-border-primary bg-surface-primary'
            }`}
        >
          {isSelected && (
            <div className='bg-surface-primary h-2 w-2 rounded-full' />
          )}
        </div>
      </div>

      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <div
          className={`size-p2 font-bold ${
            isSelected ? 'text-focus' : 'text-primary'
          }`}
        >
          {title}
        </div>
        {children}
      </div>
    </button>
  );
}
